import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import { statusOf } from '@/lib/prayer-utils';
import { shareAriaForPrayer } from '@/lib/share-aria';
import SheetShell from './SheetShell';
import UpdateComposer, { type UpdateComposerHandle } from './UpdateComposer';
import PrivateToggle from './PrivateToggle';
import SendGlyph from './SendGlyph';
import styles from './LeafActionSheet.module.css';

type Mode = 'menu' | 'update';

/**
 * RU item 1 (2026-10-20): what card 5 shows. `available`: the ordinary Delete.
 * `checking`: nothing yet (the lifecycle entry of a pending prayer is being
 * read). Otherwise the ordinary Delete cannot work for this prayer (a pending
 * prayer with a recording and no lifecycle entry; deletePrayerProtected refuses
 * it), and a plain note names what the prayer page actually offers: `remove`
 * its "Remove this prayer", `type` its "Type what you remember" (after which
 * the prayer can be deleted), `wait` nothing yet (the recording is still
 * finishing), `reopen` (fix round 1, F4) the page cannot read the recording
 * until the app is reopened, after which it can finish.
 */
export type LeafDeleteState = 'available' | 'checking' | 'remove' | 'type' | 'wait' | 'reopen';

const DELETE_UNAVAILABLE_KEY: Record<'remove' | 'type' | 'wait' | 'reopen', string> = {
  remove: 'leaf.deleteUnavailableRemove',
  type: 'leaf.deleteUnavailableType',
  wait: 'leaf.deleteUnavailableWait',
  reopen: 'leaf.deleteUnavailableReopen',
};

interface LeafActionSheetProps {
  prayer: Prayer;
  onClose: () => void;
  onSendCard: () => void;
  onAnswerAction: () => void;
  onEditTestimony: () => void;
  onTogglePrivate: (next: boolean) => void;
  onDelete: () => void;
  onReopen: () => void;
  /** RU item 1: card 5's state (default `available`, the ordinary Delete). */
  deleteState?: LeafDeleteState;
  /** Coordinator-computed z for the `leaf` layer (A2); forwarded to SheetShell.
   *  Undefined for non-migrated callers (CSS fallback 200/201). */
  zIndex?: number;
}

/**
 * Sacred Leaf action sheet — the 5-card menu opened by the floating
 * LeafButton on PrayerDetail. Cards (top to bottom):
 *   1. Add an update — expands the same SheetShell into UpdateComposer
 *   2. Send a card — bubbles to LetThemKnowSheet (gated by privacy)
 *   3. Mark as answered / Edit testimony (state-conditional)
 *   4. Hold this one close — toggle, sheet does NOT dismiss on tap
 *   5. Delete — in-place expand confirm; bubbles to PrayPage's
 *      undo-toast pattern after confirm
 *
 * Below card 5 (only when status === Answered): a thin divider + a
 * dim "Reopen this prayer" text-link (R5 c).
 *
 * Composer mode reuses the same SheetShell (no remount, no flicker)
 * with a "← back to actions" affordance that retains the draft through
 * UpdateComposer's non-destructive close handle.
 */
export default function LeafActionSheet({
  prayer,
  onClose,
  onSendCard,
  onAnswerAction,
  onEditTestimony,
  onTogglePrivate,
  onDelete,
  onReopen,
  deleteState = 'available',
  zIndex,
}: LeafActionSheetProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('menu');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const composerRef = useRef<UpdateComposerHandle | null>(null);

  const isAnswered = statusOf(prayer) === 'Answered';
  const isPrivate = !!prayer.isPrivate;

  // Keep unfinished words before returning to the actions menu. Registration
  // cleanup stops/stores capture; the shared draft owns any pending write.
  const handleComposerBack = () => {
    if (composerRef.current?.keepDraft() === false) return;
    setMode('menu');
  };

  // Ordinary close never saves or discards a new update.
  const handleBackdrop = () => {
    if (mode === 'update' && composerRef.current?.keepDraft() === false) return;
    onClose();
  };

  // A2: ADDITIVE handoff — do NOT close the leaf. The share chain pushes ABOVE
  // the still-mounted `leaf` layer (a single clean coordinator push). Closing
  // the leaf here would double-navigate against the share push and the stray
  // go(-1) would eat the push (A2-plan §2). Returning to the prayer (X /
  // completion) is `requestClose('leaf')` in PrayerDetail; Back steps share ->
  // leaf menu -> detail.
  const handleSendCard = () => {
    onSendCard();
  };

  // Card 3: A2 additive — keep the leaf mounted; the TestimonyModal pushes
  // above it. PrayerDetail's exitTestimonyToDetail (requestClose('leaf')) returns
  // to the prayer on completion; Back steps testimony -> leaf menu -> detail.
  const handleAnswerAction = () => {
    if (isAnswered) {
      onEditTestimony();
    } else {
      onAnswerAction();
    }
  };

  const handleConfirmDelete = () => {
    // PrayerDetail unmounts when the prayer leaves the store; the sheet
    // unmounts with it and the user lands on PrayPage with the 7s
    // undo-toast (PrayPage owns it).
    onDelete();
  };

  const handleReopen = () => {
    onClose();
    onReopen();
  };

  return (
    <SheetShell
      ariaLabel={t('leaf.sheetTitle')}
      onBackdrop={handleBackdrop}
      closeLabel={t('leaf.sheetCloseAria')}
      zIndex={zIndex}
      dragToDismiss
    >
      {/* Defensive GPU-compositing wrapper. iOS WebKit can paint
          GPU-promoted siblings (e.g. PrayPage's breathing-gold .reverent
          span) on top of fixed-positioned modals regardless of z-index
          unless the modal is also GPU-composited. PrayerDetail typically
          covers PrayPage so this is belt-and-braces here, but the pattern
          is documented for any future modal that may stack over GPU
          siblings — see CLAUDE.md core rule #15 backdrop note. */}
      <div className={`${styles.stackContext} ${mode === 'menu' ? styles.menuScroll : ''}`}>
        {mode === 'menu' ? (
          <>
            <h2 className={styles.sheetTitle}>{t('leaf.sheetTitle')}</h2>
            <ul className={styles.cardList}>
              {/* Card 1: Add an update */}
              <li>
                <button
                  type="button"
                  className={`${styles.card} ${styles.cardPrimary} ${styles.cardAddUpdate}`}
                  onClick={() => setMode('update')}
                >
                  <span className={styles.cardIcon}>
                    <MicGlyph />
                  </span>
                  <span className={styles.cardBody}>
                    <span className={styles.cardTitle}>{t('leaf.addUpdateTitle')}</span>
                  </span>
                </button>
              </li>

              {/* Card 2: Send a card */}
              <li>
                <button
                  type="button"
                  className={`${styles.card} ${styles.cardPrimary}`}
                  onClick={handleSendCard}
                  aria-label={shareAriaForPrayer(prayer, t)}
                >
                  <span className={styles.cardIcon}>
                    <SendGlyph />
                  </span>
                  <span className={styles.cardBody}>
                    <span className={styles.cardTitle}>{t('share.sendCard')}</span>
                    <span className={styles.cardSubtitle}>{shareAriaForPrayer(prayer, t)}</span>
                  </span>
                </button>
              </li>

              {/* Card 3: Mark answered / Edit testimony — same icon both
                  states; gold halo accent (.cardAnswered) signals the
                  answered variant per the design's "let the halo do the
                  differentiation" ruling. */}
              <li>
                <button
                  type="button"
                  className={`${styles.card} ${styles.cardPrimary} ${isAnswered ? styles.cardAnswered : ''}`}
                  onClick={handleAnswerAction}
                >
                  <span className={styles.cardIcon}>
                    <LeafGlyph />
                  </span>
                  <span className={styles.cardBody}>
                    <span className={styles.cardTitle}>
                      {isAnswered ? t('leaf.editTestimonyTitle') : t('leaf.markAnsweredTitle')}
                    </span>
                  </span>
                </button>
              </li>

              {/* Card 4: Hold close — toggle row, sheet does NOT dismiss
                  on tap. The embedded PrivateToggle owns the row's label,
                  the ✦ star, the (i) info button + popover, and the
                  on/off track. No separate cardTitle (would have rendered
                  "Hold this one close" twice — once here and once inside
                  PrivateToggle). The toggle's own .on-state styling
                  (italic label, opaque star, gold track, light thumb)
                  carries the "currently private" signal. */}
              <li>
                <div className={`${styles.card} ${styles.cardHoldClose}`}>
                  <span className={styles.cardIcon}>
                    <HeartGlyph />
                  </span>
                  <PrivateToggle
                    value={isPrivate}
                    onChange={onTogglePrivate}
                    variant="embedded"
                    className={styles.holdCloseToggle}
                  />
                </div>
              </li>

              {/* RU item 1: no ordinary Delete where it cannot work; a plain
                  note says what the prayer page offers instead. */}
              {deleteState !== 'available' && deleteState !== 'checking' && (
                <li className={styles.cardDeleteSeparated}>
                  <p className={styles.deleteUnavailable}>{t(DELETE_UNAVAILABLE_KEY[deleteState])}</p>
                </li>
              )}

              {/* Card 5: Delete — in-place expand to confirm row.
                  detail.deleteConfirm + detail.cancel + detail.deleteAction
                  reused from the existing copy. The .cardDeleteSeparated
                  margin-top breaks Tier 3 (destructive) from Tier 2 above. */}
              {deleteState === 'available' && (
              <li className={styles.cardDeleteSeparated}>
                {confirmingDelete ? (
                  <div className={`${styles.card} ${styles.cardDelete} ${styles.cardDeleteActive}`}>
                    <div className={styles.deleteConfirmRow}>
                      <p className={styles.deleteConfirmText}>{t('detail.deleteConfirm')}</p>
                      <div className={styles.deleteConfirmButtons}>
                        <button
                          type="button"
                          className={styles.deleteCancel}
                          onClick={() => setConfirmingDelete(false)}
                        >
                          {t('detail.cancel')}
                        </button>
                        <button
                          type="button"
                          className={styles.deleteConfirmBtn}
                          onClick={handleConfirmDelete}
                        >
                          {t('detail.deleteAction')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`${styles.card} ${styles.cardDelete}`}
                    onClick={() => setConfirmingDelete(true)}
                  >
                    <span className={styles.cardIcon}>
                      <TrashGlyph />
                    </span>
                    <span className={styles.cardBody}>
                      <span className={styles.cardTitle}>{t('leaf.deleteTitle')}</span>
                    </span>
                  </button>
                )}
              </li>
              )}
            </ul>

            {/* R5 (c) — Reopen text-link below card 5, only when answered.
                Reuses detail.reopen which was already present in all 3
                locales; no new key. Same handler the old answered-badge
                .reopenLink used (handleToggleAnswered reads status to
                decide direction; no celebration replay on reopen). */}
            {isAnswered && (
              <div className={styles.reopenRow}>
                <hr className={styles.reopenDivider} />
                <button
                  type="button"
                  className={styles.reopenLink}
                  onClick={handleReopen}
                  aria-label={t('detail.reopen')}
                >
                  {t('detail.reopen')}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className={styles.composerHeader}>
              <button
                type="button"
                className={styles.composerBack}
                onClick={handleComposerBack}
                aria-label={t('leaf.addUpdateBackAria')}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
              <h2 className={styles.composerTitle}>{t('leaf.addUpdateTitle')}</h2>
            </div>
            <UpdateComposer
              key={prayer.id}
              ref={composerRef}
              prayer={prayer}
              voiceTarget={`update-${prayer.id}`}
              onCommit={onClose}
              onClose={handleComposerBack}
            />
          </>
        )}
      </div>
    </SheetShell>
  );
}

// ---- Inline glyphs ----
// Kept compact and consistent with the app's existing icon vocabulary
// (Lucide-style strokes, 22×22 inside the 28×28 .cardIcon slot).

function LeafGlyph() {
  // Same path family as the answered-celebration bloom in
  // PrayerDetail.module.css — keeps the leaf grammar coherent.
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.8 2c1 3 .5 4.5-1.5 10-.5 1.5-1.5 3.5-2.5 5" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </svg>
  );
}

function MicGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );
}

function HeartGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function TrashGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}
